import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ListingCard from '../components/listings/ListingCard';
import API from '../utils/api';
import { useSEO } from '../hooks/useSEO';
import { useAuth } from '../context/AuthContext';

const CONDITIONS = [
  { value:'new',       label:'New' },
  { value:'like_new',  label:'Like New' },
  { value:'excellent', label:'Excellent' },
  { value:'very_good', label:'Very Good' },
  { value:'good',      label:'Good' },
  { value:'fair',      label:'Fair' },
];

export default function BrowsePage() {
  useSEO({ title: 'Browse Listings', description: 'Search and filter thousands of listings — rentals, auctions, sales, barters, and free items near you.' });
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [saveSearchModal, setSaveSearchModal] = useState(false);
  const [saveSearchName, setSaveSearchName] = useState('');
  const [saveSearchSaved, setSaveSearchSaved] = useState(false);

  const [listings,   setListings]   = useState([]);
  const [total,      setTotal]      = useState(0);
  const [pages,      setPages]      = useState(1);
  const [loading,    setLoading]    = useState(true);
  const [categories, setCategories] = useState([]);
  const [sidebarOpen,setSidebarOpen]= useState(false);

  // Filters
  const [filters, setFilters] = useState({
    q:          searchParams.get('q')        || '',
    type:       searchParams.get('type')     || '',
    category:   searchParams.get('category')|| '',
    minPrice:   searchParams.get('minPrice')|| '',
    maxPrice:   searchParams.get('maxPrice')|| '',
    condition:  searchParams.get('condition')|| '',
    sort:       searchParams.get('sort')    || 'newest',
    distance:   searchParams.get('distance')|| '',
    daysPosted: searchParams.get('daysPosted')|| '',
    page:       parseInt(searchParams.get('page')) || 1,
  });

  useEffect(() => { API.get('/categories').then(r => setCategories(r.data.categories)).catch(()=>{}); }, []);

  const fetchListings = useCallback(async (f = filters) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(f).forEach(([k,v]) => { if (v && k !== 'page') params.set(k, v); });
      params.set('page', f.page);
      const { data } = await API.get(`/search/listings?${params}`);
      setListings(data.results || []);
      // Note: pagination info not returned, adjust as needed
    } catch (err) {
      console.error('Search error:', err);
      setListings([]);
    }
    finally { setLoading(false); }
  }, []);

  // Watch for URL changes and update filters
  useEffect(() => {
    const updated = {
      q:          searchParams.get('q')        || '',
      type:       searchParams.get('type')     || '',
      category:   searchParams.get('category') || '',
      minPrice:   searchParams.get('minPrice') || '',
      maxPrice:   searchParams.get('maxPrice') || '',
      condition:  searchParams.get('condition') || '',
      sort:       searchParams.get('sort')     || 'newest',
      distance:   searchParams.get('distance') || '',
      daysPosted: searchParams.get('daysPosted') || '',
      page:       parseInt(searchParams.get('page')) || 1,
    };
    setFilters(updated);
    fetchListings(updated);
  }, [searchParams, fetchListings]);

  const updateFilter = (key, value) => {
    const updated = { ...filters, [key]: value, page: 1 };
    setFilters(updated);
    const params = new URLSearchParams();
    Object.entries(updated).forEach(([k,v]) => { if (v && v !== 1) params.set(k, v); });
    setSearchParams(params);
    fetchListings(updated);
  };

  const clearFilters = () => {
    const reset = { q:'', type:'', category:'', minPrice:'', maxPrice:'', condition:'', distance:'', daysPosted:'', sort:'newest', page:1 };
    setFilters(reset);
    setSearchParams({});
    fetchListings(reset);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchListings(filters);
  };

  const typeOptions = [
    { value:'',        label:'All Types',  icon:'✨' },
    { value:'rent',    label:'For Rent',   icon:'🔵' },
    { value:'auction', label:'Auction',    icon:'🔨' },
    { value:'sale',    label:'For Sale',   icon:'🟢' },
  ];

  const sortOptions = [
    { value:'newest',     label:'Newest first' },
    { value:'trending',   label:'Trending now' },
    { value:'price_low',  label:'Price: Low → High' },
    { value:'price_high', label:'Price: High → Low' },
    { value:'saved',      label:'Most saved' },
  ];

  const activeFilterCount = [filters.type, filters.category, filters.minPrice, filters.maxPrice, filters.condition, filters.distance, filters.daysPosted].filter(Boolean).length;

  return (
    <div style={{ maxWidth:1280, margin:'0 auto', padding:'32px 48px 64px', minHeight:'80vh' }}>
      {/* Header row */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:24, gap:16, flexWrap:'wrap' }}>
        <div>
          <h1 style={{ fontFamily:"'Playfair Display',serif", fontSize:28, fontWeight:700, marginBottom:4 }}>
            {filters.q ? `Results for "${filters.q}"` : filters.type === 'rent' ? 'Rentals' : filters.type === 'auction' ? 'Live Auctions' : filters.type === 'sale' ? 'For Sale' : 'Browse All Listings'}
          </h1>
          <div style={{ fontSize:14, color:'var(--text-soft)' }}>{loading ? 'Loading...' : `${total.toLocaleString()} listing${total !== 1 ? 's' : ''} found`}</div>
        </div>

        {/* Search bar */}
        <form onSubmit={handleSearch} style={{ display:'flex', gap:8, flex:1, maxWidth:400 }}>
          <input
            className="form-input"
            placeholder="Search listings..."
            value={filters.q}
            onChange={e => setFilters(p => ({...p, q: e.target.value}))}
            style={{ flex:1 }}
          />
          <button type="submit" className="btn btn-cherry btn-sm">Search</button>
        </form>

        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <select className="form-select" style={{ width:'auto', padding:'9px 32px 9px 12px', fontSize:13 }}
            value={filters.sort} onChange={e => updateFilter('sort', e.target.value)}>
            {sortOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          {user && (filters.q || activeFilterCount > 0) && (
            <button className="btn btn-ghost btn-sm" title="Save this search & get alerts" onClick={() => { setSaveSearchName(filters.q || 'My search'); setSaveSearchModal(true); setSaveSearchSaved(false); }}>
              🔔 Save Search
            </button>
          )}
          <button
            className={`btn btn-sm ${activeFilterCount ? 'btn-cherry' : 'btn-ghost'}`}
            onClick={() => setSidebarOpen(o => !o)}
          >
            🔧 Filters {activeFilterCount > 0 && `(${activeFilterCount})`}
          </button>
        </div>
      </div>

      {/* Save Search Modal */}
      {saveSearchModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.45)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }}
          onClick={e => e.target === e.currentTarget && setSaveSearchModal(false)}>
          <div style={{ background:'white', borderRadius:16, padding:28, width:380, boxShadow:'0 20px 60px rgba(0,0,0,.2)' }}>
            {saveSearchSaved ? (
              <>
                <div style={{ fontSize:40, textAlign:'center', marginBottom:12 }}>🔔</div>
                <h3 style={{ textAlign:'center', fontFamily:"'Playfair Display',serif", fontSize:18, marginBottom:8 }}>Search saved!</h3>
                <p style={{ textAlign:'center', fontSize:13, color:'var(--text-soft)', marginBottom:20 }}>We'll email you when new listings match.</p>
                <div style={{ display:'flex', gap:10 }}>
                  <button className="btn btn-ghost btn-sm" style={{ flex:1 }} onClick={() => navigate('/dashboard/watchlist')}>View Watchlist</button>
                  <button className="btn btn-cherry btn-sm" style={{ flex:1 }} onClick={() => setSaveSearchModal(false)}>Done</button>
                </div>
              </>
            ) : (
              <>
                <h3 style={{ fontFamily:"'Playfair Display',serif", fontSize:18, marginBottom:6 }}>Save this search</h3>
                <p style={{ fontSize:13, color:'var(--text-soft)', marginBottom:16 }}>Get an email when new listings match your current filters.</p>
                <input className="form-input" placeholder="Name this search..." value={saveSearchName}
                  onChange={e => setSaveSearchName(e.target.value)} style={{ marginBottom:16 }} />
                <div style={{ display:'flex', gap:10 }}>
                  <button className="btn btn-ghost btn-sm" style={{ flex:1 }} onClick={() => setSaveSearchModal(false)}>Cancel</button>
                  <button className="btn btn-cherry btn-sm" style={{ flex:1 }} onClick={async () => {
                    await API.post('/watchlist/searches', {
                      name: saveSearchName || filters.q || 'My search',
                      query: filters.q,
                      filters: { type: filters.type, category: filters.category, minPrice: filters.minPrice, maxPrice: filters.maxPrice, condition: filters.condition },
                    });
                    setSaveSearchSaved(true);
                  }}>Save & Get Alerts</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Type tabs */}
      <div style={{ display:'flex', gap:8, marginBottom:24, flexWrap:'wrap' }}>
        {typeOptions.map(t => (
          <button key={t.value}
            className={`btn btn-sm ${filters.type === t.value ? 'btn-cherry' : 'btn-ghost'}`}
            onClick={() => updateFilter('type', t.value)}
          >{t.icon} {t.label}</button>
        ))}
      </div>

      <div style={{ display:'flex', gap:24 }}>
        {/* Sidebar */}
        {sidebarOpen && (
          <div style={{ width:240, flexShrink:0 }}>
            <div style={{ background:'white', border:'1px solid var(--border)', borderRadius:14, padding:20, position:'sticky', top:88 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
                <div style={{ fontWeight:700, fontSize:14 }}>Filters</div>
                {activeFilterCount > 0 && (
                  <button onClick={clearFilters} style={{ fontSize:12, color:'var(--cherry)', background:'none', border:'none', cursor:'pointer', fontWeight:600 }}>Clear all</button>
                )}
              </div>

              {/* Category */}
              <div style={{ marginBottom:20, paddingBottom:20, borderBottom:'1px solid var(--border)' }}>
                <div style={{ fontSize:11, fontWeight:700, color:'var(--text-soft)', textTransform:'uppercase', letterSpacing:'1px', marginBottom:10 }}>Category</div>
                <select className="form-select" style={{ fontSize:13 }} value={filters.category} onChange={e => updateFilter('category', e.target.value)}>
                  <option value="">All Categories</option>
                  {categories.map(c => <option key={c.id} value={c.slug}>{c.icon} {c.label}</option>)}
                </select>
              </div>

              {/* Price range */}
              <div style={{ marginBottom:20, paddingBottom:20, borderBottom:'1px solid var(--border)' }}>
                <div style={{ fontSize:11, fontWeight:700, color:'var(--text-soft)', textTransform:'uppercase', letterSpacing:'1px', marginBottom:10 }}>Price Range</div>
                <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                  <input className="form-input" placeholder="Min" type="number" value={filters.minPrice}
                    onChange={e => updateFilter('minPrice', e.target.value)} style={{ fontSize:13 }} />
                  <span style={{ color:'var(--text-soft)', fontSize:12 }}>—</span>
                  <input className="form-input" placeholder="Max" type="number" value={filters.maxPrice}
                    onChange={e => updateFilter('maxPrice', e.target.value)} style={{ fontSize:13 }} />
                </div>
              </div>

              {/* Condition */}
              <div style={{ marginBottom:20, paddingBottom:20, borderBottom:'1px solid var(--border)' }}>
                <div style={{ fontSize:11, fontWeight:700, color:'var(--text-soft)', textTransform:'uppercase', letterSpacing:'1px', marginBottom:10 }}>Condition</div>
                <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                  {CONDITIONS.map(c => (
                    <label key={c.value} style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, cursor:'pointer' }}>
                      <input type="radio" name="condition" value={c.value}
                        checked={filters.condition === c.value}
                        onChange={() => updateFilter('condition', c.value)}
                        style={{ accentColor:'var(--cherry)' }} />
                      {c.label}
                    </label>
                  ))}
                  {filters.condition && (
                    <button onClick={() => updateFilter('condition','')} style={{ fontSize:12,color:'var(--cherry)',background:'none',border:'none',cursor:'pointer',textAlign:'left',padding:0 }}>Clear condition</button>
                  )}
                </div>
              </div>

              {/* Distance */}
              <div style={{ marginBottom:20, paddingBottom:20, borderBottom:'1px solid var(--border)' }}>
                <div style={{ fontSize:11, fontWeight:700, color:'var(--text-soft)', textTransform:'uppercase', letterSpacing:'1px', marginBottom:10 }}>Within (miles)</div>
                <select className="form-select" style={{ fontSize:13 }} value={filters.distance} onChange={e => updateFilter('distance', e.target.value)}>
                  <option value="">Any distance</option>
                  <option value="5">5 miles</option>
                  <option value="10">10 miles</option>
                  <option value="25">25 miles</option>
                  <option value="50">50 miles</option>
                </select>
              </div>

              {/* Date Posted */}
              <div style={{ marginBottom:20 }}>
                <div style={{ fontSize:11, fontWeight:700, color:'var(--text-soft)', textTransform:'uppercase', letterSpacing:'1px', marginBottom:10 }}>Posted</div>
                <select className="form-select" style={{ fontSize:13 }} value={filters.daysPosted} onChange={e => updateFilter('daysPosted', e.target.value)}>
                  <option value="">Any time</option>
                  <option value="1">Today</option>
                  <option value="7">This week</option>
                  <option value="30">This month</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Grid */}
        <div style={{ flex:1, minWidth:0 }}>
          {loading ? (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))', gap:18 }}>
              {Array.from({length:8}).map((_,i) => (
                <div key={i} style={{ background:'var(--cream)', borderRadius:14, height:280, animation:'pulse 1.5s ease-in-out infinite' }} />
              ))}
            </div>
          ) : listings.length === 0 ? (
            <div style={{ textAlign:'center', padding:'80px 40px' }}>
              <div style={{ fontSize:48, marginBottom:16 }}>🔍</div>
              <h3 style={{ fontFamily:"'Playfair Display',serif", fontSize:24, fontWeight:700, marginBottom:8 }}>No listings found</h3>
              <p style={{ fontSize:14, color:'var(--text-soft)', marginBottom:20 }}>Try adjusting your filters or search terms</p>
              <button className="btn btn-ghost" onClick={clearFilters}>Clear all filters</button>
            </div>
          ) : (
            <>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))', gap:18 }}>
                {listings.map((l,i) => <ListingCard key={l.id} listing={l} delay={i * 0.04} />)}
              </div>

              {/* Pagination */}
              {pages > 1 && (
                <div style={{ display:'flex', justifyContent:'center', gap:8, marginTop:40 }}>
                  <button className="btn btn-ghost btn-sm" disabled={filters.page <= 1} onClick={() => updateFilter('page', filters.page - 1)}>← Prev</button>
                  {Array.from({length:Math.min(pages,7)}).map((_,i) => {
                    const p = i + 1;
                    return (
                      <button key={p} className={`btn btn-sm ${filters.page === p ? 'btn-cherry' : 'btn-ghost'}`}
                        onClick={() => updateFilter('page', p)}>{p}</button>
                    );
                  })}
                  <button className="btn btn-ghost btn-sm" disabled={filters.page >= pages} onClick={() => updateFilter('page', filters.page + 1)}>Next →</button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
